import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TreeNode } from 'primeng/primeng';
import 'rxjs/add/operator/toPromise';
import { Observable } from 'rxjs/Observable';

import { AuthenticationService } from '../authentication/authentication.service';
import { LoggingService } from '../logging/logging.service';

import { IFile } from '../../models/outModels/File';
import { CreateFolderModel } from '../../models/inModels/CreateFolderModel';
// tslint:disable-next-line:max-line-length
import {
	TagFileModel, DescribeFileModel, UploadLinkModel, RenameFileModel, RenameDirectoryModel, MoveDirectoryModel,
	AddDocumentAuthor,
	UpdatePlansModel,
	UpdateInterestsModel,
	LearningCourse,
	CoursePath
} from '../../models/inModels/TagFileModel';
import { UpdateDirectoryMetaModel } from '../../models/inModels/TagFileModel';
import { IFolder } from '../../models/outModels/File';
import { ReorderDirectoryFilesModel } from '../../models/inModels/TagFileModel';
import { Assessment } from '../../models/outModels/Question';
import { AddObjectiveModel } from "../../models/inModels/TagFileModel";


@Injectable()
export class StorageService {
	uploadUrl: string;
	downloadUrl: string;
	sbcUploadUrl: string;
	viewUrl: string;
	imageUrl: string;
	useLocalStorage = false;
	constructor(
		private http: HttpClient,
		private auth: AuthenticationService,
		private logging: LoggingService) {
		this.uploadUrl = `${this.auth.apiUrl}/api/storage/upload`;
		this.downloadUrl = `${this.auth.apiUrl}/api/storage/download`;
		this.viewUrl = `${this.auth.apiUrl}/api/storage/view`;
		this.imageUrl = `${this.auth.apiUrl}/api/storage/image`;

		var user = this.auth.getUser();
		if (user) {
			this.useLocalStorage = this.auth.getUser().remember;
		}
	}

	uploadToSbc(name: string, file: Blob) {
		const formData = new FormData();
		formData.append('file', file, name);
		return this.http.post(`${this.sbcUploadUrl}`, formData, { headers: this.auth.headerAuth })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	setRequestHeaders(event): void {
		event.xhr.setRequestHeader('Authorization', `Bearer ${this.auth.accesToken}`);
	}

	handleError(event, errors): any {
		errors.push({ severity: 'error', summary: 'Error', detail: this.logging.handleErrorXhr(event.xhr.response) });
	}

	handleSuccess(event): any {
		return JSON.parse(event.xhr.response);
	}

	downloadFile(id: string): Observable<Blob> {
		const options = { headers: this.auth.header, reportProgress: true, responseType: 'blob' as 'blob' };
		return this.http.get(`${this.auth.apiUrl}/api/storage/download/${id}`, options);
	}

	viewFile(id: string): Observable<Blob> {
		const options = { headers: this.auth.header, reportProgress: true, responseType: 'blob' as 'blob' };
		return this.http.get(`${this.auth.apiUrl}/api/storage/view/${id}`, options);
	}

	getRootDirectory(includeUnpublished = false) {
		return this.http.get(`${this.auth.apiUrl}/api/storage/folder?includeUnpublished=${includeUnpublished}`, { headers: this.auth.header })
			.toPromise()
			.then(response => <TreeNode[]>response)
			.catch(error => this.logging.handleError(error));
	}

	getDirectory(id: number, includeUnpublished = false): Promise<IFolder> {
		return this.http.get(`${this.auth.apiUrl}/api/storage/folder/${id}?includeUnpublished=${includeUnpublished}`, { headers: this.auth.header })
			.toPromise()
			.then(response => <IFolder>response)
			.catch(error => this.logging.handleError(error));
	}

	getDirectoryParent(id: number): Promise<IFolder> {
		return this.http.get(`${this.auth.apiUrl}/api/storage/folder/${id}/parent`, { headers: this.auth.header })
			.toPromise()
			.then(response => <IFolder>response)
			.catch(error => this.logging.handleError(error));
	}

	getDirectoryContent(id: number): Promise<IFile[]> {
		return this.http.get(`${this.auth.apiUrl}/api/storage/folder/${id}/content`, { headers: this.auth.header })
			.toPromise()
			.then(response => response as IFile[])
			.catch(error => this.logging.handleError(error));
	}

	createDirectory(model: CreateFolderModel) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/folder/create`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	renameDirectory(model: RenameDirectoryModel) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/folder/name`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	updateDirectory(model: UpdateDirectoryMetaModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/storage/folder/update`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response as TreeNode)
			.catch(error => this.logging.handleError(error));
	}

	moveDirectory(model: MoveDirectoryModel) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/folder/move`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	reorderDirectoryFiles(model: ReorderDirectoryFilesModel) {
		console.log(model);
		return this.http.post(`${this.auth.apiUrl}/api/storage/files/reorder`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	deleteDirectory(id: number) {
		return this.http.get(`${this.auth.apiUrl}/api/storage/folder/${id}/delete`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	publishDirectory(id: number) {
		return this.http.get(`${this.auth.apiUrl}/api/storage/folder/${id}/publish`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getAssessment(id: number): Promise<Assessment> {
		return this.http.get(`${this.auth.apiUrl}/api/storage/folder/${id}/assessment`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	moveFile(model: MoveDirectoryModel) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/file/move`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	deleteFile(id: number) {
		return this.http.get(`${this.auth.apiUrl}/api/storage/file/delete/${id}`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	tagDocument(model: TagFileModel) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/file/tag`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	removeTagFromDocument(model: TagFileModel) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/file/tag/remove`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	renameDocument(model: RenameFileModel) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/file/name`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	describeDocument(model: DescribeFileModel) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/file/description`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	updateAuthor(model: AddDocumentAuthor) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/file/author`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	updatePlans(model: UpdatePlansModel) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/file/plans`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	updateCoverImages(documentId: number, coverImageId: string, featuredImageIds: string[]) {
		const featuredImage = featuredImageIds.length > 0 ? featuredImageIds.join(',') : null;
		const model = { documentId, coverImageId, featuredImageIds: featuredImage };
		return this.http.post(`${this.auth.apiUrl}/api/storage/file/cover/image`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	flagDesign(documentId: string, isDesigned: boolean) {
		const model = { documentId, isDesigned }
		return this.http.post(`${this.auth.apiUrl}/api/storage/file/flag/design`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}
	uploadLink(model: UploadLinkModel) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/upload/link`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getObjectives(id: number): Promise<any> {
		return this.http.get(`${this.auth.apiUrl}/api/storage/folder/${id}/objectives`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}


	addObjective(model: AddObjectiveModel) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/folder/objective/add`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	deleteObjective(id: number): Promise<string[]> {
		return this.http.get(`${this.auth.apiUrl}/api/storage/folder/objective/delete/${id}`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	//#region Table Storage
	private tableDataKey = "tableData";

	clearTable(component: string) {
		if (this.useLocalStorage) {
			localStorage.removeItem(`${this.tableDataKey}${component}`);
		} else {
			sessionStorage.removeItem(`${this.tableDataKey}${component}`);
		}
	}

	addInterestToDoc(model: UpdateInterestsModel) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/file/add/interest`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	removeInterest(model: UpdateInterestsModel) {
		return this.http.post(`${this.auth.apiUrl}/api/storage/file/remove/interest`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getInterestsList(): Promise<any> {
		return this.http.get<any[]>(`${this.auth.apiUrl}/api/interests/dropdown`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getLearningCourseList(): Promise<any> {
		return this.http.get<any[]>(`${this.auth.apiUrl}/api/storage/dropdown`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	addLearningCourse(course: LearningCourse): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/learning-courses/create`, JSON.stringify(course), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}


	addCoursePath(course: CoursePath ): Promise<any> {
			return this.http.post(`${this.auth.apiUrl}/api/course-paths/create`, JSON.stringify(course), { headers: this.auth.header })
				.toPromise()
				.then(response => response)
				.catch(error => this.logging.handleError(error));
		}
	// UPDATE COURSE
	updateLearningCourse(course: LearningCourse): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/learning-courses/update`, JSON.stringify(course), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}
	updateCoursePath(course: CoursePath): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/course-paths/update`, JSON.stringify(course), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}


	deleteLearningCourse(id: number): Promise<any> {
		return this.http.get(`${this.auth.apiUrl}/api/learning-courses/delete/${id}`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

getCoursePaths(): Observable<CoursePath[]> {
  return this.http.get<CoursePath[]>(`${this.auth.apiUrl}/api/course-paths/list`, {
    headers: this.auth.header,
    params: new HttpParams().set('t', Date.now().toString()) 
  });
}
deleteCoursePath(id: number): Promise<any> {
		return this.http.get(`${this.auth.apiUrl}/api/course-paths/delete/${id}`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

}



// WEBPACK FOOTER //
// ./src/app/services/system/storage.service.ts